import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Phone, Lock, Security } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import logo from '../assets/logo.png';

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    emailOrPhone: '',
    password: '',
    otp: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSendOTP = async () => {
    if (!formData.emailOrPhone || !formData.password) {
      setError('Please enter email/phone and password first');
      return;
    }

    setLoading(true);
    try {
      await authService.sendLoginOTP(formData.emailOrPhone);
      setOtpSent(true);
      setSuccess('OTP sent successfully! Please check your email/SMS.');
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.emailOrPhone || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (!otpSent) {
      await handleSendOTP();
      return;
    }

    if (!formData.otp) {
      setError('Please enter the OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.verifyLogin(
        formData.emailOrPhone,
        formData.password,
        formData.otp
      );
      
      localStorage.setItem('authToken', response.token || response.access);
      localStorage.setItem('userInfo', JSON.stringify(response.user));
      
      // Check user type and redirect accordingly
      if (response.user.is_superuser) {
        setSuccess('Admin login successful! Redirecting...');
        setTimeout(() => {
          navigate('/admin');
        }, 1500);
      } else if (response.user.is_staff) {
        setSuccess('Staff login successful! Redirecting...');
        setTimeout(() => {
          navigate('/staff');
        }, 1500);
      } else {
        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };



  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            padding: { xs: 3, sm: 5 },
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            maxWidth: '480px',
            margin: '0 auto'
          }}
        >
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <img 
              src={logo} 
              alt="TICSPL Logo" 
              style={{ 
                height: '100px', 
                marginBottom: '24px',
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
              }} 
            />
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 800,
                color: '#1a237e',
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem' },
                letterSpacing: '-0.02em'
              }}
            >
              Welcome Back!
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ 
                fontSize: '1.2rem',
                fontWeight: 400,
                color: '#424242',
                lineHeight: 1.4
              }}
            >
              Please sign in to your account
            </Typography>
          </Box>

          {/* Alert Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {success}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleLogin}>
            {/* Email/Phone Field */}
            <TextField
              fullWidth
              name="emailOrPhone"
              label="Email Address / Phone Number"
              value={formData.emailOrPhone}
              onChange={handleInputChange}
              margin="normal"
              required
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {formData.emailOrPhone && formData.emailOrPhone.includes('@') ? 
                      <Email color="action" /> : 
                      <Phone color="action" />
                    }
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }
              }}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              name="password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              required
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }
              }}
            />

            {/* OTP Field - Shows after OTP is sent */}
            {otpSent && (
              <TextField
                fullWidth
                name="otp"
                label="Enter OTP"
                value={formData.otp}
                onChange={handleInputChange}
                margin="normal"
                required
                disabled={loading}
                placeholder="Enter 6-digit OTP"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Security color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  }
                }}
                helperText="Check your email/SMS for the OTP"
              />
            )}

            {/* Login Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 4,
                mb: 2,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 3,
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                  boxShadow: '0 10px 30px rgba(25, 118, 210, 0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : otpSent ? (
                'Verify & Login'
              ) : (
                'Send OTP & Login'
              )}
            </Button>

            {/* Resend OTP Button */}
            {otpSent && (
              <Button
                fullWidth
                variant="outlined"
                onClick={handleSendOTP}
                disabled={loading}
                sx={{ mb: 2 }}
              >
                Resend OTP
              </Button>
            )}

            {/* Simple Login for Testing */}
            <Button
              fullWidth
              variant="text"
              onClick={async () => {
                if (!formData.emailOrPhone || !formData.password) {
                  setError('Please enter username and password');
                  return;
                }
                setLoading(true);
                try {
                  const response = await authService.simpleLogin(formData.emailOrPhone, formData.password);
                  localStorage.setItem('authToken', response.token);
                  localStorage.setItem('userInfo', JSON.stringify(response.user));
                  
                  if (response.user.is_superuser) {
                    navigate('/admin');
                  } else {
                    navigate('/dashboard');
                  }
                } catch (err) {
                  setError('Simple login failed: ' + (err.response?.data?.message || err.message));
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              sx={{ mb: 2, color: '#666' }}
            >
              Quick Login (No OTP)
            </Button>

            {/* Register Link */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography 
                variant="body1" 
                sx={{
                  color: '#666666',
                  fontSize: '1rem',
                  fontWeight: 400
                }}
              >
                Don't have an account?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/register')}
                  sx={{
                    fontWeight: 600,
                    textDecoration: 'none',
                    color: '#1976d2',
                    fontSize: '1rem',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: '#1565c0'
                    }
                  }}
                >
                  Sign up here
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;